import { Link } from "react-router-dom";
import {
  Lightbulb,
  Rocket,
  Users,
  ShieldCheck,
  ArrowRight,
} from "@phosphor-icons/react";

const VALUES = [
  {
    icon: Lightbulb,
    title: "Innovation First",
    description:
      "We build tools — including Atlas, our AI assistant — that cut down on busywork so teams can focus on what matters.",
  },
  {
    icon: Users,
    title: "Team Empowerment",
    description:
      "Built for collaboration. Every feature is designed to make teams more aligned, transparent, and productive.",
  },
  {
    icon: Rocket,
    title: "Ship With Speed",
    description:
      "We believe the best tools are the ones that get out of your way. Simple, fast, and reliable — always.",
  },
  {
    icon: ShieldCheck,
    title: "Trust & Security",
    description:
      "Enterprise-grade security with SOC 2 compliance, end-to-end encryption, and granular access controls.",
  },
];

const STATS = [
  { value: "50+", label: "Team Members" },
  { value: "2,000+", label: "Teams Served" },
  { value: "12M+", label: "Tasks Managed" },
  { value: "99.9%", label: "Uptime SLA" },
];

export function AboutPage() {
  return (
    <main className="pt-28 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 bg-primary-500/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-500 dark:border-primary-400/20 dark:bg-primary-400/5 dark:text-primary-300">
          About Opero
        </span>
        <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-5xl">
          We're building the future of{" "}
          <span className="gradient-text">team productivity</span>
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
          Opero was born from a simple idea: task management shouldn't feel like
          a chore. We combine AI intelligence with thoughtful design to help
          teams ship faster, collaborate better, and stay in flow.
        </p>
      </section>

      {/* Mission */}
      <section className="mx-auto max-w-4xl px-6 mt-20">
        <div className="rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-10 sm:p-14">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400">
            Our Mission
          </h2>
          <p className="mt-4 text-2xl font-semibold leading-relaxed text-slate-900 dark:text-white">
            To eliminate the friction between ideas and execution — so every
            team can focus on doing their best work, not managing work about
            work.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-5xl px-6 mt-20">
        <h2 className="text-center text-sm font-semibold uppercase tracking-wider text-primary-500 dark:text-primary-400">
          Our Values
        </h2>
        <h3 className="mt-3 text-center text-3xl font-bold text-slate-900 dark:text-white">
          What drives us every day
        </h3>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {VALUES.map((value) => (
            <div
              key={value.title}
              className="group rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-8 transition-all duration-300 hover:border-primary-500/30 dark:hover:border-primary-400/20 hover:shadow-lg"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20 transition-transform duration-300 group-hover:scale-110">
                <value.icon
                  size={24}
                  weight="duotone"
                  className="text-primary-500 dark:text-primary-400"
                />
              </div>
              <h4 className="mt-5 text-lg font-semibold text-slate-900 dark:text-white">
                {value.title}
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-4xl px-6 mt-20">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-3xl font-bold gradient-text">{stat.value}</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 mt-20">
        <div className="rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 p-10 sm:p-14 text-center">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Ready to transform how your team works?
          </h2>
          <p className="mt-4 text-slate-300 max-w-lg mx-auto">
            Join thousands of teams already using Opero to ship faster and
            collaborate better.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-primary-500 shadow-lg transition-all duration-200 hover:bg-slate-50"
            >
              Start Free Trial
              <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href="mailto:corporate@digiits.com"
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-white/10"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
